import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  ClipboardCheck,
  Loader2,
  GripVertical,
  Sparkles,
  Wand2
} from "lucide-react";

const QUESTION_TYPES = [
  { value: "mcq", label: "Multiple Choice" },
  { value: "true_false", label: "True / False" },
  { value: "short_answer", label: "Short Answer" },
  { value: "coding", label: "Coding" },
];

const CATEGORIES = [
  "Technology", "Healthcare", "Finance", "Marketing", "Customer Service",
  "Management", "Engineering", "Design", "Sales", "Education",
  "Legal", "Human Resources", "Data Science", "Logistics", "Other",
];

const emptyQuestion = () => ({
  id: crypto.randomUUID(),
  question_type: "mcq",
  question_text: "",
  options: ["", "", "", ""],
  correct_answer: "",
  points: 10,
  code_template: "",
});

const AssessmentCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Technology",
    skill_tags: "",
    time_limit: 30,
    passing_score: 70,
    difficulty: "intermediate",
    is_public: true,
  });

  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiForm, setAiForm] = useState({ job_title: "", skills: "", question_count: 10 });

  const generateWithAI = async () => {
    if (!aiForm.job_title.trim()) return toast.error("Enter a job title");
    setAiGenerating(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/assessments/generate-ai`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          job_title: aiForm.job_title,
          skills: aiForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
          difficulty: form.difficulty,
          question_count: aiForm.question_count,
          category: form.category,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setForm((f) => ({
          ...f,
          title: data.title || f.title,
          description: data.description || f.description,
          skill_tags: (data.skill_tags || []).join(", "),
        }));
        if (data.questions?.length > 0) {
          setQuestions(data.questions.map((q) => ({
            id: crypto.randomUUID(),
            question_type: q.question_type || "mcq",
            question_text: q.question_text || "",
            options: q.options || ["", "", "", ""],
            correct_answer: q.correct_answer || "",
            points: q.points || 10,
            code_template: q.code_template || "",
          })));
        }
        setShowAiPanel(false);
        toast.success(`Generated ${data.questions?.length || 0} questions!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "AI generation failed");
      }
    } catch {
      toast.error("Failed to generate with AI");
    } finally {
      setAiGenerating(false);
    }
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, [field]: value } : q)));
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx ? { ...q, options: q.options.map((o, j) => (j === oIdx ? value : o)) } : q
      )
    );
  };

  const addOption = (qIdx) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIdx ? { ...q, options: [...q.options, ""] } : q))
    );
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx ? { ...q, options: q.options.filter((_, j) => j !== oIdx) } : q
      )
    );
  };

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);

  const removeQuestion = (idx) => {
    if (questions.length <= 1) return toast.error("Need at least 1 question");
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    if (!form.title.trim()) return "Title is required";
    if (!form.description.trim()) return "Description is required";
    if (questions.length === 0) return "Add at least one question";
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) return `Question ${i + 1}: text is required`;
      if (!q.correct_answer.trim()) return `Question ${i + 1}: correct answer is required`;
      if ((q.question_type === "mcq" || q.question_type === "true_false") && q.options.some((o) => !o.trim()))
        return `Question ${i + 1}: all options must be filled`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return toast.error(err);

    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        ...form,
        skill_tags: form.skill_tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        questions: questions.map((q) => ({
          question_type: q.question_type,
          question_text: q.question_text,
          options: q.question_type === "mcq" || q.question_type === "true_false" ? q.options.filter((o) => o.trim()) : null,
          correct_answer: q.correct_answer,
          points: q.points,
          code_template: q.question_type === "coding" ? q.code_template : null,
        })),
      };

      const res = await fetch(`${API}/assessments`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Assessment created successfully!");
        navigate("/assessments");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to create assessment");
      }
    } catch {
      toast.error("Failed to create assessment");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";
  const labelClass = "block text-xs text-gray-400 mb-1.5";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/assessments")} className="p-2 hover:bg-white/5 rounded" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-crimson" />
                Create Assessment
              </h1>
              <p className="text-sm text-gray-400">Build a custom skills test for candidates</p>
            </div>
          </div>

          {/* AI Generation Panel */}
          <div className="bg-gradient-to-r from-crimson/10 to-transparent border border-crimson/20 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-crimson" />
                <h3 className="font-semibold text-sm">AI-Powered Generation</h3>
              </div>
              <Button
                onClick={() => setShowAiPanel(!showAiPanel)}
                variant="outline"
                size="sm"
                className="border-crimson/30 text-crimson hover:bg-crimson/10 text-xs"
                data-testid="toggle-ai-panel"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1" />
                {showAiPanel ? "Hide" : "Generate with AI"}
              </Button>
            </div>
            {showAiPanel && (
              <div className="space-y-3 pt-2">
                <p className="text-xs text-gray-400">Describe the role and AI will generate assessment questions automatically.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Job title (e.g., React Developer)"
                    value={aiForm.job_title}
                    onChange={(e) => setAiForm({ ...aiForm, job_title: e.target.value })}
                    className={inputClass}
                    data-testid="ai-job-title"
                  />
                  <input
                    type="text"
                    placeholder="Skills (comma-sep): react, typescript"
                    value={aiForm.skills}
                    onChange={(e) => setAiForm({ ...aiForm, skills: e.target.value })}
                    className={inputClass}
                    data-testid="ai-skills"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={aiForm.question_count}
                      onChange={(e) => setAiForm({ ...aiForm, question_count: parseInt(e.target.value) || 5 })}
                      min={3}
                      max={25}
                      className={`${inputClass} w-20`}
                      data-testid="ai-count"
                    />
                    <Button
                      onClick={generateWithAI}
                      disabled={aiGenerating}
                      className="flex-1 bg-crimson hover:bg-crimson/90 text-white text-xs"
                      data-testid="ai-generate-btn"
                    >
                      {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      {aiGenerating ? "Generating..." : "Generate"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Assessment Details */}
          <div className="bg-obsidian-light border border-[#222] rounded-lg p-6 space-y-4">
            <h2 className="font-semibold text-lg">Assessment Details</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., JavaScript Fundamentals"
                  className={inputClass}
                  data-testid="assessment-title-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what this assessment covers..."
                  rows={3}
                  className={inputClass}
                  data-testid="assessment-desc-input"
                />
              </div>
              <div>
                <label className={labelClass}>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass} data-testid="assessment-category">
                  {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Difficulty</label>
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className={inputClass} data-testid="assessment-difficulty">
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Time Limit (minutes)</label>
                <input type="number" value={form.time_limit} onChange={(e) => setForm({ ...form, time_limit: parseInt(e.target.value) || 10 })} min={5} max={180} className={inputClass} data-testid="assessment-time" />
              </div>
              <div>
                <label className={labelClass}>Passing Score (%)</label>
                <input type="number" value={form.passing_score} onChange={(e) => setForm({ ...form, passing_score: parseInt(e.target.value) || 50 })} min={1} max={100} className={inputClass} data-testid="assessment-passing" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Skill Tags (comma separated)</label>
                <input
                  type="text"
                  value={form.skill_tags}
                  onChange={(e) => setForm({ ...form, skill_tags: e.target.value })}
                  placeholder="javascript, react, frontend"
                  className={inputClass}
                  data-testid="assessment-tags"
                />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                  className="accent-crimson w-4 h-4"
                  id="is-public"
                  data-testid="assessment-public"
                />
                <label htmlFor="is-public" className="text-sm text-gray-300">Make this assessment publicly available</label>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg">Questions ({questions.length})</h2>
              <Button onClick={addQuestion} variant="outline" size="sm" className="border-[#333] text-gray-300 hover:bg-white/5" data-testid="add-question-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Question
              </Button>
            </div>

            {questions.map((q, qi) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-4"
                data-testid={`question-block-${qi}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <GripVertical className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-300">Question {qi + 1}</span>
                  </div>
                  <button onClick={() => removeQuestion(qi)} className="p-1.5 text-gray-500 hover:text-rose-400" data-testid={`remove-q-${qi}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Question Text</label>
                    <input
                      type="text"
                      value={q.question_text}
                      onChange={(e) => updateQuestion(qi, "question_text", e.target.value)}
                      placeholder="Enter your question..."
                      className={inputClass}
                      data-testid={`q-text-${qi}`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Type</label>
                    <select
                      value={q.question_type}
                      onChange={(e) => {
                        const type = e.target.value;
                        updateQuestion(qi, "question_type", type);
                        if (type === "true_false") updateQuestion(qi, "options", ["True", "False"]);
                        else if (type === "mcq" && q.options.length < 2) updateQuestion(qi, "options", ["", "", "", ""]);
                      }}
                      className={inputClass}
                      data-testid={`q-type-${qi}`}
                    >
                      {QUESTION_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                    </select>
                  </div>
                </div>

                {/* MCQ / True-False Options */}
                {(q.question_type === "mcq" || q.question_type === "true_false") && (
                  <div className="space-y-2">
                    <label className={labelClass}>Options</label>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(qi, oi, e.target.value)}
                          placeholder={`Option ${oi + 1}`}
                          className={`${inputClass} flex-1`}
                          disabled={q.question_type === "true_false"}
                          data-testid={`q-opt-${qi}-${oi}`}
                        />
                        {q.question_type === "mcq" && q.options.length > 2 && (
                          <button onClick={() => removeOption(qi, oi)} className="p-1.5 text-gray-500 hover:text-rose-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    {q.question_type === "mcq" && q.options.length < 6 && (
                      <button onClick={() => addOption(qi)} className="text-xs text-crimson hover:underline flex items-center gap-1 mt-1">
                        <Plus className="w-3 h-3" /> Add Option
                      </button>
                    )}
                  </div>
                )}

                {/* Coding template */}
                {q.question_type === "coding" && (
                  <div>
                    <label className={labelClass}>Code Template (shown to candidate)</label>
                    <textarea
                      value={q.code_template}
                      onChange={(e) => updateQuestion(qi, "code_template", e.target.value)}
                      placeholder="def solution():\n    # your code here\n    pass"
                      rows={4}
                      className={`${inputClass} font-mono text-xs`}
                      data-testid={`q-template-${qi}`}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>
                      Correct Answer
                      {(q.question_type === "mcq" || q.question_type === "true_false") && (
                        <span className="text-gray-600 ml-1">(must match an option exactly)</span>
                      )}
                    </label>
                    {q.question_type === "true_false" ? (
                      <select
                        value={q.correct_answer}
                        onChange={(e) => updateQuestion(qi, "correct_answer", e.target.value)}
                        className={inputClass}
                        data-testid={`q-answer-${qi}`}
                      >
                        <option value="">Select...</option>
                        <option value="True">True</option>
                        <option value="False">False</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={q.correct_answer}
                        onChange={(e) => updateQuestion(qi, "correct_answer", e.target.value)}
                        placeholder={q.question_type === "short_answer" ? "answer1|answer2 (pipe-separated)" : "Exact matching option text"}
                        className={inputClass}
                        data-testid={`q-answer-${qi}`}
                      />
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Points</label>
                    <input
                      type="number"
                      value={q.points}
                      onChange={(e) => updateQuestion(qi, "points", parseInt(e.target.value) || 1)}
                      min={1}
                      max={100}
                      className={inputClass}
                      data-testid={`q-points-${qi}`}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pb-8">
            <Button onClick={() => navigate("/assessments")} variant="outline" className="flex-1 border-[#333] text-gray-300 hover:bg-white/5">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-crimson hover:bg-crimson/90 text-white" data-testid="save-assessment-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Create Assessment
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssessmentCreator;
